import { Component, EventEmitter, Input, Output } from '@angular/core';
import { DialogService } from 'src/app/core/dialog.service';
import { Order, OrderStatus } from 'src/app/core/orders.service';
import { UtilsService } from 'src/app/core/utils.service';
import { ORDERS_TABLE_COLUMNS } from 'src/app/shared/constants/operationals';
import { OrdersQineticDialogComponent } from '../orders-qinetic-dialog/orders-qinetic-dialog.component';
import { OrdersDecidoDialogComponent } from '../orders-decido-dialog/orders-decido-dialog.component';
import { OrdersQineticReloadDialogComponent } from '../orders-qinetic-reload-dialog/orders-qinetic-reload-dialog.component';
import { ErrorService, SplittypayError } from 'src/app/core/error.service';
import { OrdersQineticErrorDialogComponent } from '../orders-qinetic-error-dialog/orders-qinetic-error-dialog.component';
import { OrdersAmountDialogComponent } from '../orders-amount-dialog/orders-amount-dialog.component';
import { OrdersDeleteDialogComponent } from '../orders-delete-dialog/orders-delete-dialog.component';
import { OrdersRequestsDialogComponent } from '../orders-requests-dialog/orders-requests-dialog.component';
import { NavigationService } from 'src/app/core/navigation.service';

@Component({
  selector: 'app-orders-data-table',
  templateUrl: './orders-data-table.component.html',
  styleUrls: ['./orders-data-table.component.scss'],
})
export class OrdersDataTableComponent {
  @Input() orders: Order[] = [];
  @Output() refresh: EventEmitter<void> = new EventEmitter<void>();
  @Output() scrolledToEnd: EventEmitter<void> = new EventEmitter<void>();
  displayed_columns: string[] = ORDERS_TABLE_COLUMNS;
  running_order_status: OrderStatus = OrderStatus.running;
  canceled_order_status: OrderStatus = OrderStatus.canceled;
  pending_order_status: OrderStatus = OrderStatus.pending;
  store_review_order_status: OrderStatus = OrderStatus.store_review;
  refunded_order_status: OrderStatus = OrderStatus.refunded;

  constructor(
    private utils_service: UtilsService,
    private dialog_service: DialogService,
    private error_service: ErrorService,
    private navigation_service: NavigationService,
  ) {}

  onCopyClick(content: string): void {
    this.utils_service.copyClick(content);
  }

  onScroll(event: Event) {
    const element = event.target as HTMLElement;
    const end_offset = 200;
    if (element) {
      if (element.offsetHeight + element.scrollTop >= element.scrollHeight - end_offset) {
        this.scrolledToEnd.emit();
      }
    }
  }

  onQineticClick(order: Order): void {
    this.dialog_service.openDialog(OrdersQineticDialogComponent, {
      qinetic_position_id: order.qinetic_position_id,
      buyer_id: order.buyer_id,
      order_id: order.order_id,
    });
  }

  onDecidoClick(order: Order, decido_evaluation_id: string): void {
    this.dialog_service.openDialog(OrdersDecidoDialogComponent, {
      decido_evaluation_id,
      buyer_id: order.buyer_id,
    });
  }

  onQineticReloadClick(order: Order): void {
    this.dialog_service
      .openDialog(OrdersQineticReloadDialogComponent, { order })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onPositionErrorClick(order: Order): void {
    this.dialog_service.openDialog(OrdersQineticErrorDialogComponent, { position_error: order.position_error });
  }

  onOrderAmountClick(order: Order): void {
    const order_amount = order.amount?.total;
    const order_currency = this.getCurrencyLabel(order);
    this.dialog_service.openDialog(OrdersAmountDialogComponent, {
      order_amount,
      order_currency,
      buyer_id: order.buyer_id,
    });
  }

  onDeleteClick(order: Order): void {
    this.dialog_service
      .openDialog(OrdersDeleteDialogComponent, { order })
      ?.afterClosed()
      .subscribe(dialog_result => {
        if (dialog_result && dialog_result.success) {
          this.refresh.emit();
        }

        if (dialog_result && dialog_result.error) {
          const processed_error: SplittypayError = dialog_result.error;
          this.error_service.processApiError(processed_error.code, processed_error.message);
        }
      });
  }

  onRequestsClick(order: Order): void {
    this.dialog_service.openDialog(OrdersRequestsDialogComponent, {
      buyer_id: order.buyer_id,
      order_id: order.order_id,
    });
  }

  onDetailsClick(order: Order): void {
    this.navigation_service.toOrderDetails(order.store_id as string, order.order_id as string);
  }

  getVbanStatus(order: Order) {}

  getCurrencyLabel(order: Order): string {
    return this.utils_service.getCurrencyLabelFromOrder(order);
  }

  getOrderStatusLabel(status?: OrderStatus): string {
    return this.utils_service.getOrderStatusLabel(status);
  }

  getOrderStatusClass(status: OrderStatus): string {
    return this.utils_service.getOrderStatusClass(status);
  }

  getPaymentLink(order?: Order): string {
    if (!order) return 'N/A';

    if (order.short_url) {
      return order.short_url.replaceAll(' ', '%20');
    } else {
      return this.utils_service.getBuyerAppLinkFromOrder(order);
    }
  }

  getEvaluationClass(result: string): string {
    if (result.toUpperCase() == 'DEL-POS') {
      return 'sp-color__green';
    }
    return 'sp-color__red';
  }

  getPaymentMethodLogo(order: Order): string {
    let prefix = '../../assets/img/payment_methods/';

    if (order?.buyer_qinetic_payment_source?.qinetic_sepa_debit?.iban) {
      return prefix + 'sepa-logo.png';
    }

    const funding = order?.buyer_stripe_payment_source?.stripe_card?.brand;
    switch (funding) {
      case 'mastercard':
        return prefix + 'mastercard-logo.png';
      case 'visa':
        return prefix + 'visa-logo.png';
      case 'amex':
        return prefix + 'american-express-logo.png';
      default:
        return '';
    }
  }

  getFullName(order: Order): string {
    return `${order.buyer_firstname} ${order.buyer_lastname}`;
  }

  getLastOrderStatusChange(last_status_change: string | undefined): string {
    return this.utils_service.getLastOrderStatusChange(last_status_change);
  }
}
